import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import { usePostHog } from '@posthog/react';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './CookieConsent.css';

export const CookieConsentComponent = () => {
  const posthog = usePostHog();

  useEffect(() => {
    CookieConsent.run({
      // Enable dark mode and auto theme
      mode: 'opt-in',
      autoShow: true,
      
      // Cookie categories
      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        },
        analytics: {
          enabled: false
        },
        marketing: {
          enabled: false
        }
      },

      // Language settings
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              // "Manage preferences" lives inline at the end of the description
              // (no footer strip or third button) to keep the banner compact
              // on mobile.
              description: `
                Meals You Love uses cookies to enhance your experience, analyze site usage, and assist with our marketing efforts. You can customize your cookie preferences below.
                <a href="#" data-cc="show-preferencesModal">Manage preferences</a>
              `,
              acceptAllBtn: 'Accept All',
              acceptNecessaryBtn: 'Accept Necessary Only',
            },
            preferencesModal: {
              title: 'Cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Accept necessary only',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'Cookie Usage',
                  description: 'We use cookies to ensure the basic functionalities of our meal planning service and to enhance your experience. You may choose which categories of cookies to allow.',
                },
                {
                  title: 'Strictly Necessary Cookies',
                  description: 'These cookies are essential for the proper functioning of our service. They include authentication, security, and basic functionality cookies.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Analytics Cookies',
                  description: 'These cookies allow us to analyze how you use our service so we can measure and improve its performance.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'Marketing Cookies', 
                  description: 'These cookies are used to deliver personalized content and measure the effectiveness of our marketing campaigns.',
                  linkedCategory: 'marketing'
                }
              ]
            }
          }
        }
      },

      // GUI options
      guiOptions: {
        consentModal: {
          layout: 'box inline',
          position: 'bottom right',
          equalWeightButtons: true,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: true,
          flipButtons: false
        }
      },

      onConsent: ({ cookie }) => {
        // Enable/disable analytics based on consent
        if (CookieConsent.acceptedCategory('analytics')) {
          enableAnalytics(posthog);
        } else {
          disableAnalytics(posthog);
        }

        // Enable/disable marketing based on consent
        if (CookieConsent.acceptedCategory('marketing')) {
          enableMarketing();
        } else {
          disableMarketing();
        }
      },

      onChange: ({ cookie, changedCategories }) => {
        // Handle category changes
        if (changedCategories.includes('analytics')) {
          if (CookieConsent.acceptedCategory('analytics')) {
            enableAnalytics(posthog);
          } else {
            disableAnalytics(posthog);
          }
        }

        if (changedCategories.includes('marketing')) {
          if (CookieConsent.acceptedCategory('marketing')) {
            enableMarketing();
          } else {
            disableMarketing();
          }
        }
      }
    });

    // Check if user has already made consent choices
    // If they opted out of analytics, disable PostHog immediately
    if (!CookieConsent.acceptedCategory('analytics')) {
      disableAnalytics(posthog);
    }

    // Cleanup function - don't reset consent on component unmount
    return () => {
      // No cleanup needed - let the consent persist across page reloads
    };
  }, [posthog]);

  return null; // This component doesn't render anything itself
};

// Helper functions for enabling/disabling services
const enableAnalytics = (posthogInstance?: any) => {
  // Enable PostHog tracking
  if (posthogInstance) {
    posthogInstance.opt_in_capturing();
  }
};

const disableAnalytics = (posthogInstance?: any) => {
  // Disable PostHog tracking
  if (posthogInstance) {
    posthogInstance.opt_out_capturing();
  }
};

const enableMarketing = () => {
  // Enable marketing tools
  
  // Example: Enable marketing pixels
  // if (typeof gtag !== 'undefined') {
  //   gtag('consent', 'update', {
  //     ad_storage: 'granted'
  //   });
  // }
};

const disableMarketing = () => {
  // Disable marketing tools
  
  // Example: Disable marketing pixels
  // if (typeof gtag !== 'undefined') {
  //   gtag('consent', 'update', {
  //     ad_storage: 'denied'
  //   });
  // }
};

// Export helper function to show preferences modal
export const showCookiePreferences = () => {
  CookieConsent.showPreferences();
};

// Export helper function to check if category is accepted
export const isCategoryAccepted = (category: string) => {
  return CookieConsent.acceptedCategory(category);
};
